import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, Check, Loader2 } from 'lucide-react';
import { useCommodities } from '../../utils/api-hooks/commodity';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import { scrollSelectedIntoView } from '../../utils/scrollToSelected';

/**
 * MultiSelectCommoditySelector - Multi-select dropdown for commodities
 *
 * Features:
 * - Multi-select with checkboxes
 * - Category tabs (Fruits, Vegetables) like alerts page
 * - Large expandable panel
 * - "Select All" / "Clear All" functionality
 * - Selected count display
 */
const MultiSelectCommoditySelector = ({
  values = [],
  onChange,
  placeholder = 'Select commodities...',
}) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const { commodities, isLoading, isError, error } = useCommodities();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Fruits');
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Sort categories alphabetically
  const categoryKeys = useMemo(() => {
    return Object.keys(commodities).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }, [commodities]);

  // Sort commodities within the selected category alphabetically
  const currentCommodities = useMemo(() => {
    return [...(commodities[selectedCategory] || [])].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }, [commodities, selectedCategory]);

  // Get all commodities flattened
  const allCommodities = useMemo(() => {
    const items = [];
    Object.values(commodities).forEach(categoryItems => {
      items.push(...categoryItems);
    });
    return items;
  }, [commodities]);

  // On open, jump to the category holding the first selected commodity.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const lower = values.map((v) => v.toLowerCase());
    const match = categoryKeys.find((cat) =>
      (commodities[cat] || []).some((c) => lower.includes(c.toLowerCase()))
    );
    if (match) setSelectedCategory(match);
    // Only on open: later selection changes must not switch the category.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Centre the first selected row once the (possibly switched) list is rendered.
  useLayoutEffect(() => {
    if (isOpen) scrollSelectedIntoView(listRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedCategory]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (commodityName) => {
    const alreadySelected = values.some(v => v.toLowerCase() === commodityName.toLowerCase());
    const newValues = alreadySelected
      ? values.filter(v => v.toLowerCase() !== commodityName.toLowerCase())
      : [...values, commodityName];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    onChange([...allCommodities]);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const isAllSelected = allCommodities.length > 0 && allCommodities.every(
    c => values.some(v => v.toLowerCase() === c.toLowerCase())
  );

  const CommodityCheckbox = ({ commodity, checked, onToggle }) => (
    <div
      data-selected={checked}
      className="flex items-start gap-2 cursor-pointer py-1"
      onClick={() => onToggle(commodity)}
    >
      <div
        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
          checked
            ? 'border-bloom-green bg-bloom-green'
            : 'border-[#828382] bg-transparent'
        }`}
      >
        {checked && <Check size={14} className="text-white" />}
      </div>
      <span
        className="font-normal text-[#282A29] dark:text-[#f0f5f2] break-words"
        style={{ fontSize: fontSize - 1 }}
      >
        {commodity}
      </span>
    </div>
  );

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`w-full h-10 px-4 py-2 border border-bloom-green rounded-lg bg-transparent flex items-center justify-between font-medium text-[#828382] dark:text-[#5d625f] transition-colors ${
          values.length > 0 ? 'text-[#282A29] dark:text-[#f0f5f2]' : ''
        } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-bloom-green/5'}`}
        style={{ fontSize: fontSize - 1 }}
      >
        <span className="truncate">
          {isLoading
            ? t('common.loading')
            : values.length === 0
              ? placeholder
              : `${values.length} ${t('filters.selected', 'selected')}`}
        </span>
        {isLoading ? (
          <Loader2 size={18} className="text-[#828382] animate-spin flex-shrink-0 ml-2" />
        ) : (
          <ChevronUp
            size={18}
            className={`text-bloom-green transition-transform flex-shrink-0 ml-2 ${
              isOpen ? '' : 'rotate-180'
            }`}
          />
        )}
      </button>

      {/* Expandable Panel - Positioned absolutely to overlay */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-bloom-green rounded-lg min-h-[250px] max-h-[400px] overflow-hidden flex w-full bg-white dark:bg-[#191919] shadow-lg">
          {/* Loading State */}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 size={32} className="text-bloom-green animate-spin" />
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center px-4">
                <p className="text-red-400 mb-2" style={{ fontSize }}>
                  Failed to load commodities
                </p>
                <p className="text-[#828382]" style={{ fontSize: fontSize - 2 }}>
                  {error?.message || 'Please try again'}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !isError && (
            <>
              {/* Category Labels - Left Side */}
              <div className="flex-shrink-0 w-[100px] sm:w-[120px] py-4 px-3 border-r border-bloom-green/20 bg-[#F0F5F2] dark:bg-[#232121]">
                <div className="flex flex-col gap-2">
                  {categoryKeys.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`text-left transition-colors py-1 ${
                        selectedCategory === category
                          ? 'font-semibold text-bloom-green'
                          : 'font-normal text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2]'
                      }`}
                      style={{ fontSize: fontSize - 1 }}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {/* Select All / Clear All */}
                <div className="mt-6 pt-4 border-t border-bloom-green/20 space-y-2">
                  <button
                    onClick={handleSelectAll}
                    disabled={isAllSelected}
                    className="w-full text-left text-bloom-green hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontSize: fontSize - 2 }}
                  >
                    {t('alerts.selectAll')}
                  </button>
                  <button
                    onClick={handleClearAll}
                    disabled={values.length === 0}
                    className="w-full text-left text-[#ef4444] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontSize: fontSize - 2 }}
                  >
                    {t('alerts.clearAll')}
                  </button>
                </div>
              </div>

              {/* Scrollable Commodity List - Right Side */}
              <div ref={listRef} className="flex-1 overflow-y-auto p-4 min-w-0">
                <style>{`
                  .commodity-scroll::-webkit-scrollbar {
                    width: 4px;
                  }
                  .commodity-scroll::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  .commodity-scroll::-webkit-scrollbar-thumb {
                    background: #828382;
                    border-radius: 4px;
                  }
                `}</style>
                <div className="flex flex-col gap-y-2 commodity-scroll">
                  {currentCommodities.map((commodity, index) => (
                    <CommodityCheckbox
                      key={`${selectedCategory}-${index}`}
                      commodity={commodity}
                      checked={values.some(v => v.toLowerCase() === commodity.toLowerCase())}
                      onToggle={handleToggle}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
};

export default MultiSelectCommoditySelector;
